import { BADGE_BY_ID, TIER_COLORS } from '@goaltree/backend/convex/lib/progression'
import { cn } from '@goaltree/ui/lib/utils'
import {
  Award,
  Brain,
  CalendarCheck,
  CalendarDays,
  CheckCheck,
  Droplets,
  Flag,
  Flame,
  Footprints,
  Gem,
  Lightbulb,
  Lock,
  type LucideIcon,
  MessageCircle,
  Moon,
  Mountain,
  Phone,
  Rocket,
  RotateCcw,
  Snowflake,
  Sparkles,
  Sprout,
  Sun,
  Sunrise,
  Target,
  TreeDeciduous,
  TreePine,
  Trees,
  Trophy,
  UserPlus,
  Users,
} from 'lucide-react'
import type { CSSProperties } from 'react'

export const BADGE_ICONS: Record<string, LucideIcon> = {
  award: Award,
  brain: Brain,
  'calendar-check': CalendarCheck,
  'calendar-days': CalendarDays,
  'check-check': CheckCheck,
  droplets: Droplets,
  flag: Flag,
  flame: Flame,
  footprints: Footprints,
  gem: Gem,
  lightbulb: Lightbulb,
  'message-circle': MessageCircle,
  moon: Moon,
  mountain: Mountain,
  phone: Phone,
  rocket: Rocket,
  'rotate-ccw': RotateCcw,
  snowflake: Snowflake,
  sparkles: Sparkles,
  sprout: Sprout,
  sun: Sun,
  sunrise: Sunrise,
  target: Target,
  'tree-deciduous': TreeDeciduous,
  'tree-pine': TreePine,
  trees: Trees,
  trophy: Trophy,
  'user-plus': UserPlus,
  users: Users,
}

const SIZES = {
  xs: { box: 'size-4', icon: 'size-2.5' },
  sm: { box: 'size-9', icon: 'size-4' },
  md: { box: 'size-12', icon: 'size-5' },
  lg: { box: 'size-24', icon: 'size-10' },
}

// A badge as a round medal tinted by its growth tier; oak and ancient get a
// second ring so rarity reads at a glance.
export function BadgeMedal({
  id,
  locked = false,
  size = 'md',
  className,
}: {
  id: string
  locked?: boolean
  size?: keyof typeof SIZES
  className?: string
}) {
  const def = BADGE_BY_ID[id]
  if (!def) return null
  const Icon = BADGE_ICONS[def.icon] ?? Sprout
  const rare = (def.tier === 'oak' || def.tier === 'ancient') && size !== 'xs'
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full',
        'bg-[color-mix(in_srgb,var(--tier)_16%,transparent)] text-[var(--tier)] ring-1 ring-[color-mix(in_srgb,var(--tier)_45%,transparent)]',
        rare && 'ring-2 ring-offset-2 ring-offset-background',
        locked && 'opacity-35 grayscale',
        SIZES[size].box,
        className,
      )}
      style={{ '--tier': TIER_COLORS[def.tier] } as CSSProperties}
    >
      {def.tier === 'ancient' && !locked && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-[conic-gradient(from_200deg,transparent,color-mix(in_srgb,var(--tier)_35%,transparent),transparent_60%)]"
        />
      )}
      <Icon className={cn('relative', SIZES[size].icon)} strokeWidth={2.2} />
      {locked && size !== 'sm' && (
        <span className="absolute -right-0.5 -bottom-0.5 flex size-4 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Lock className="size-2.5" strokeWidth={2.5} />
        </span>
      )}
    </span>
  )
}
